import React from 'react';
import { Button, Loading } from '@appkit4/react-components';
import Alert from '../../assets/images/Alert.svg';
import './SandpackErrorOverlay.scss';
import { ErrorClassification } from '@utils/errorClassifier';

export interface SandpackErrorOverlayProps {
    classification: ErrorClassification;
    isFixing: boolean;
    attemptNumber: number;
    maxAttempts: number;
    isRetryingOriginal?: boolean;
    onManualRetry?: () => void;
    onDismiss?: () => void;
}

const SandpackErrorOverlay: React.FC<SandpackErrorOverlayProps> = ({
    classification,
    isFixing,
    attemptNumber,
    maxAttempts,
    isRetryingOriginal = false,
    onManualRetry,
    onDismiss,
}) => {
    const { isRecoverable, errorType, confidenceScore, context } = classification;

    const getErrorTitle = () => {
        if (isFixing) {
            if (isRetryingOriginal) {
                return `Retrying Generation (Attempt ${attemptNumber}/${maxAttempts})`;
            }
            return `Fixing Error (Attempt ${attemptNumber}/${maxAttempts})`;
        }

        if (!isRecoverable) {
            if (errorType === 'network') {
                return 'Network Connection Lost';
            }
            return 'Unable to Load Project';
        }

        if (attemptNumber >= maxAttempts) {
            return 'Generation Failed';
        }

        return 'Error Detected';
    };

    const getErrorMessage = () => {
        if (isFixing) {
            if (isRetryingOriginal) {
                return 'Retrying your prompt with a fresh attempt...';
            }
            return 'AI is analyzing and fixing the error...';
        }

        if (!isRecoverable) {
            if (errorType === 'network') {
                return 'The network connection was lost while loading dependencies. Please check your internet connection and refresh the page to try again.';
            }
            return 'This project encountered a non-recoverable error. This might be due to network issues, bundler timeouts, or system constraints.';
        }

        if (attemptNumber >= maxAttempts) {
            return `Could not generate code successfully after ${maxAttempts} attempts. You may try again or modify your prompt.`;
        }

        return `A ${errorType} error was detected. The AI will attempt to fix it automatically.`;
    };

    const getConfidenceMessage = () => {
        if (isFixing || !isRecoverable || attemptNumber >= maxAttempts) {
            return null;
        }

        const confidencePercent = Math.round(confidenceScore * 100);
        let confidenceLevel = 'Low';
        let confidenceClass = 'low';

        if (confidenceScore >= 0.8) {
            confidenceLevel = 'High';
            confidenceClass = 'high';
        } else if (confidenceScore >= 0.5) {
            confidenceLevel = 'Medium';
            confidenceClass = 'medium';
        }

        return (
            <div className={`confidence-indicator ${confidenceClass}`}>
                <span className="confidence-label">Fix Confidence: </span>
                <span className="confidence-value">
                    {confidenceLevel} ({confidencePercent}%)
                </span>
            </div>
        );
    };

    const showActions = !isFixing && (attemptNumber >= maxAttempts || !isRecoverable);

    return (
        <div className="sandpack-error-overlay">
            <div className="error-content">
                <div className="error-icon">{isFixing ? <Loading indeterminate size="large" /> : <Alert />}</div>

                <h2 className="error-title">{getErrorTitle()}</h2>

                <p className="error-message">{getErrorMessage()}</p>

                {getConfidenceMessage()}

                {isFixing && (
                    <div className="fixing-status">
                        <Loading indeterminate size="small" />
                        <span>Please wait while AI fixes the issue...</span>
                    </div>
                )}

                {showActions && (
                    <div className="error-actions">
                        {onManualRetry && isRecoverable && (
                            <Button kind="primary" onClick={onManualRetry} aria-label="Retry fixing the error">
                                Retry Fix
                            </Button>
                        )}
                        {onDismiss && (
                            <Button kind="secondary" onClick={onDismiss} aria-label="Dismiss error overlay">
                                Dismiss
                            </Button>
                        )}
                    </div>
                )}

                {context && !isFixing && (
                    <details className="error-details">
                        <summary>Error Details</summary>
                        <pre className="error-context">{context}</pre>
                    </details>
                )}
            </div>
        </div>
    );
};

export default SandpackErrorOverlay;
